import CompareIcon from '@mui/icons-material/Compare'
import { Box, CardContent, CircularProgress, Divider, Typography } from '@mui/material'
import FlowApiClient from '../../backend/sub_api_clients/FlowApiClient'
import { INode } from '../../definitions/Nodes'
import { IExecutionEnvironment, INodeComparison, ISimpleExecutionEnvironment } from '../../definitions/NodesExecution'
import FlowUIManager from '../../managers/FlowUIManager'
import SmartCard from '../controls/cards/SmartCard'
import NodesIcon from '../flows/nodes/NodesIcon'
import useQuery, { buildQueryKey } from '../hooks/useQuery'
import Tile from '../layout/Tile'

interface P {
    execEnv: IExecutionEnvironment
    comparedExecEnv?: ISimpleExecutionEnvironment
}

export default function SummaryExecEnvFlowComparison({ execEnv, comparedExecEnv }: P) {
    const { status, response } = useQuery({
        queryKey: buildQueryKey('get-flow-execution-' + execEnv.uuid + '-' + comparedExecEnv.uuid),
        queryFn: () => FlowApiClient.getTotalExecEnvComparison(execEnv.uuid, comparedExecEnv.uuid),
        cache: false
    })

    const getSimilarityColor = (percentage: number) => {
        if (status === 'loading')
            return 'inherit'

        if (percentage === 100)
            return 'primary'
        else if (percentage >= 80)
            return 'success'
        else if (percentage >= 50)
            return 'warning'
        else
            return 'error'
    }

    const similarityUI = (similarity: number, isLoading: boolean) => {
        return (
            <Box key={similarity + '' + isLoading + ''} position='relative' flex={1} display='flex'>
                <CircularProgress variant={isLoading ? 'indeterminate' : 'determinate'} color={getSimilarityColor(similarity)} value={similarity} />
                <Box position='absolute' justifyContent='center' alignItems='center' display='flex' width='100%' height='100%' top={0} left={0}>
                    {!isLoading && <Typography variant='caption'>{similarity}%</Typography>}
                </Box>
            </Box>
        )
    }

    const renderNodeUI = (node: INode) => {
        const myColor = FlowUIManager.getNodeColor(node?.type)

        return (
            <SmartCard
                flex={1}
                style={{ borderLeft: `${node?.type === 'start' ? undefined : `var(--unit) solid ${myColor}`}`,
                    padding: 'var(--unit)',
                    borderTopLeftRadius: 'calc(var(--unit) - 1px)',
                    borderBottomLeftRadius: 'calc(var(--unit) - 1px)' }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'start', gap: 'calc(var(--unit) * 2)', padding: 'var(--unit)' }}>
                    <NodesIcon nodeType={node?.type} size={25} />
                    {status !== 'loading'
                        ? <Typography sx={{ display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>{node?.name ?? 'No Node'}</Typography>
                        : (
                                <Box width='100%' sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                                    <CircularProgress size={16} />
                                </Box>
                            )}
                </div>
            </SmartCard>
        )
    }

    const renderRow = (comparison: INodeComparison) => {
        return (
            <Box sx={{ display: 'flex', flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box flex={1} display='flex' padding={2}>
                    {renderNodeUI(comparison?.currentNode)}
                </Box>
                <Box gap={2} flexDirection='row' flex={1.5} display='flex' alignItems='center' padding={2}>
                    <Divider sx={{ flex: 1, height: 2, borderBottomWidth: 4 }} />
                    <Box sx={{ display: 'flex', flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between' }}>
                        {similarityUI(comparison?.similarity, status === 'loading')}
                    </Box>
                    <Divider sx={{ flex: 1, height: 2, borderBottomWidth: 4 }} />
                </Box>
                <Box flex={1} display='flex' padding={2}>
                    {renderNodeUI(comparison?.comparedNode)}
                </Box>
            </Box>
        )
        return null
    }

    return (
        <SmartCard flex={1}>
            <CardContent style={{ height: '80vh', overflowY: 'auto', gap: 10 }}>
                <Tile.Column>
                    <Box sx={{ display: 'flex', flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', marginBottom: 2 }}>
                        <Box sx={{ display: 'flex', flexDirection: 'row', alignItems: 'center' }}>
                            <CompareIcon />
                            <Typography variant='h6' marginLeft={1}>Total comparison</Typography>
                        </Box>
                        <Box sx={{ display: 'flex', flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between' }}>
                            {similarityUI(response?.totalSimilarity, status === 'loading')}
                            <Typography variant='h4' marginLeft={1}>Similarity</Typography>
                        </Box>
                    </Box>
                    <Divider />
                    <Box>
                        {response?.nodesSimilarity?.map(x => {
                            return renderRow(x)
                        })}
                    </Box>
                </Tile.Column>
            </CardContent>
        </SmartCard>
    )
}
